import { formatTrayFraction } from "../lib/api";
import { getCropColor, radius, tokens } from "../styles/theme";

const FRACTION_EPSILON = 1e-6;

export type TrayIconCycle = {
  id: string;
  crop: string;
  fraction: number;
};

// BRO-699: visual encoding of partial-tray cycles. The icon's grid is
// set by the *smallest* fraction currently on the tray so adding or
// removing a larger fraction never re-lays out the tile. Bigger
// fractions span adjacent cells; empty cells use a low-contrast
// background. Aspect ratio matches a real 10x20 tray (long axis
// horizontal).
//
// Grid map:
//   - 1     → 1×1
//   - 1/2   → 2×1 (two square cells along long axis)
//   - 1/4   → 4×1 (four vertical stripes)
//   - 1/8   → 4×2 (eight squares)
export function TrayIcon({ cycles, size = "md" }: { cycles: TrayIconCycle[]; size?: "sm" | "md" }) {
  const smallest = cycles.length === 0 ? 1 : cycles.reduce((m, c) => Math.min(m, c.fraction), 1);

  let cols = 1;
  let rows = 1;
  if (smallest <= 0.125 + FRACTION_EPSILON) {
    cols = 4;
    rows = 2;
  } else if (smallest <= 0.25 + FRACTION_EPSILON) {
    cols = 4;
    rows = 1;
  } else if (smallest <= 0.5 + FRACTION_EPSILON) {
    cols = 2;
    rows = 1;
  }
  const totalCells = cols * rows;

  const cells: (string | null)[] = new Array(totalCells).fill(null);
  let cursor = 0;
  for (const c of cycles) {
    const span = Math.max(1, Math.round(c.fraction * totalCells));
    const color = getCropColor(c.crop);
    for (let i = 0; i < span && cursor < totalCells; i += 1) {
      cells[cursor] = color;
      cursor += 1;
    }
  }

  const filledLabel = cycles.map((c) => `${c.crop} ${formatTrayFraction(c.fraction)}`).join(", ");
  const emptyCells = cells.filter((c) => c === null).length;
  const emptyLabel = emptyCells > 0 ? `, ${formatTrayFraction(emptyCells / totalCells)} empty` : "";

  const dims =
    size === "sm"
      ? { width: 64, gap: "1px", padding: "1px" }
      : {
          width: 132,
          gap: "2px",
          padding: "2px",
        };

  return (
    <div
      role="img"
      aria-label={`Tray layout: ${filledLabel || "empty"}${emptyLabel}`}
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(${cols}, 1fr)`,
        gridTemplateRows: `repeat(${rows}, 1fr)`,
        width: `${dims.width}px`,
        aspectRatio: "2 / 1",
        gap: dims.gap,
        background: tokens.lineStrong,
        padding: dims.padding,
        borderRadius: radius.sm,
        border: `1px solid ${tokens.lineStrong}`,
      }}
    >
      {cells.map((color, idx) => (
        <span
          key={idx}
          aria-hidden
          style={{
            background: color ?? tokens.sunken,
            borderRadius: size === "sm" ? "2px" : radius.xs,
          }}
        />
      ))}
    </div>
  );
}
